import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import { Tabs, TabsList, Tab, TabPanel } from '@/components/tabs';
import {
  Toolbar,
  ToolbarHeading,
  ToolbarDescription,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { RouteListings } from './components/RouteListings';
import { VersionListings } from './components/VersionListings';
import PublishVersionModal from './modal/PublishVersionModal';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useConfirmPublishMutation,
  useManualPublishUpdateMutation,
} from '@/services/RoutingManagementService';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useSnackbar } from 'notistack';
import { waitForPublishConfirmation } from './utils/publishConfirmation';

const RoutingManagement = () => {
  const { currentLayout } = useLayout();
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [manualPublishUpdate, { isLoading: isPublishingManual }] =
    useManualPublishUpdateMutation();
  const [confirmPublish] = useConfirmPublishMutation();
  const [isManualPublishDialogOpen, setIsManualPublishDialogOpen] =
    useState(false);
  const [manualPublishError, setManualPublishError] = useState<string | null>(
    null
  );
  const [isConfirmingManualPublish, setIsConfirmingManualPublish] =
    useState(false);

  const handlePublishVersion = () => {
    setIsPublishModalOpen(true);
  };

  const handlePublishSuccess = () => {
    setIsPublishModalOpen(false);
  };

  const handlePublishManualRoutes = () => {
    setManualPublishError(null);
    setIsManualPublishDialogOpen(true);
  };

  const handleCloseManualPublishDialog = () => {
    if (isPublishingManual || isConfirmingManualPublish) {
      return;
    }

    setManualPublishError(null);
    setIsManualPublishDialogOpen(false);
  };

  const handleConfirmPublishManualRoutes = async () => {
    try {
      setManualPublishError(null);
      setIsConfirmingManualPublish(true);
      await manualPublishUpdate().unwrap();

      const confirmationResponse = await waitForPublishConfirmation({
        confirmPublish: () => confirmPublish().unwrap(),
      });

      if (!confirmationResponse.success) {
        setManualPublishError('Something went wrong');
        return;
      }

      enqueueSnackbar('Manual routes published successfully', {
        variant: 'success',
      });
      setIsManualPublishDialogOpen(false);
    } catch (error) {
      console.error('Failed to publish manual routes', error);
      const publishError = error as {
        data?: { message?: string };
        message?: string;
      };
      setManualPublishError(
        publishError?.data?.message ||
          publishError?.message ||
          'Failed to publish manual routes'
      );
    } finally {
      setIsConfirmingManualPublish(false);
    }
  };

  return (
    <Fragment>
      {hasPermission('routing_management_read') && (
        <>
          {currentLayout?.name === 'demo1-layout' && (
            <Container>
              <Toolbar>
                <ToolbarHeading>
                  <ToolbarPageTitle id="routing-management-heading" />
                  <ToolbarDescription>
                    Manage routes and their published versions.
                  </ToolbarDescription>
                </ToolbarHeading>
              </Toolbar>
            </Container>
          )}
          <Container>
            <Tabs defaultValue={1} className="w-full">
              <div className="flex justify-between items-center gap-5 mb-5 flex-col sm:flex-row">
                <TabsList className="flex gap-6">
                  <Tab value={1} className="text-[14px]">
                    Route Management
                  </Tab>
                  <Tab value={2} className="text-[14px]">
                    Version Management
                  </Tab>
                </TabsList>
                <div className="flex gap-2">
                  {hasPermission('routing_management_update') && (
                    <>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handlePublishVersion}
                        aria-label="Publish Version"
                      >
                        Publish Version
                      </button>
                      <button
                        className="btn btn-sm btn-light border border-gray-300"
                        onClick={handlePublishManualRoutes}
                        aria-label="Publish Manual Routes"
                        disabled={isPublishingManual}
                      >
                        Publish Manual Routes
                      </button>
                    </>
                  )}
                </div>
              </div>
              <TabPanel value={1}>
                <RouteListings />
              </TabPanel>
              <TabPanel value={2}>
                <VersionListings />
              </TabPanel>
            </Tabs>
          </Container>
          <PublishVersionModal
            isOpen={isPublishModalOpen}
            onClose={() => setIsPublishModalOpen(false)}
            onPublishSuccess={handlePublishSuccess}
          />
          <ConfirmationDialog
            open={isManualPublishDialogOpen}
            onClose={handleCloseManualPublishDialog}
            onConfirm={handleConfirmPublishManualRoutes}
            message={
              <span>
                {manualPublishError && (
                  <>
                    <span className="block text-red-600 font-medium mb-3">
                      {manualPublishError}
                    </span>
                  </>
                )}
                Publishing manual routes will make the most recent manual route
                changes available to users.
                <br />
                <br />
                <strong>Note:</strong> This process may take a few minutes to
                complete.
                <br />
                Do you want to publish the current manual routes now?
              </span>
            }
            cancelButtonText="Cancel"
            confirmButtonText="Publish Manual Routes"
            confirmButtonVariant="default"
            disabled={isPublishingManual || isConfirmingManualPublish}
            showReasonCode={false}
            isLoading={isPublishingManual || isConfirmingManualPublish}
          />
        </>
      )}
    </Fragment>
  );
};

export default RoutingManagement;
